import { useManager } from 'dreamstate';
import { ReactNode, useCallback, useEffect } from 'react';

import {
  rem,
  Box,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  TabIndicator,
} from '@vimeo/bokeh';
import { ReviewQuestion } from '@vimeo/bokeh-icons';
import { SceneItemOverlay } from '@vimeo/cloud-live/components/shared/general';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { QnaReplyForm } from '@vimeo/live-core/components/interaction/qna/form/QnaReplyForm';
import { NewQuestionManageItem } from '@vimeo/live-core/components/interaction/qna/question_item/NewQuestionManageItem';
import { QuestionsList } from '@vimeo/live-core/components/interaction/qna/questions_list/QuestionsList';
import { SessionInactive } from '@vimeo/live-core/components/interaction/qna/session/SessionInactive';
import { useQnaPanels } from '@vimeo/live-core/components/interaction/qna/useQnaPanels';
import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { EmptyStatePlaceholder } from '@vimeo/live-core/components/panels/empty';
import {
  TAB_LIST_COLUMN_ROW_ITEM_STYLE,
  TABS_STYLES,
  TAB_PANELS_STYLES,
  TAB_PANEL_STYLES,
} from '@vimeo/live-core/components/panels/styles';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { HorizontalScrollShadow } from '@vimeo/live-core/components/scroll_shadow';
import { useTabsMapping } from '@vimeo/live-core/hooks/bokeh/useTabsMapping';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '@vimeo/live-core/hooks/live/useActionConfirmation';
import { InteractionFirebaseManager } from '@vimeo/live-core/store/firebase/base';
import {
  QnAManager,
  canReplyQuestion,
  hasQuestionReplies,
  useQnaResponding,
} from '@vimeo/live-core/store/firebase/interaction/qna';
import {
  trackApproveModeratedQuestion,
  trackArchiveQuestion,
  trackPinQuestion,
  trackSwitchActiveTab,
  trackUnarchiveQuestion,
  trackUnpinQuestion,
  trackViewQna,
} from '@vimeo/live-core/tracking/tracking_engagement_qna';
import { trackVenuesViewQa } from '@vimeo/live-core/tracking/tracking_venues_engagement';
import {
  T_NO_QUESTIONS_YET,
  T_QUESTION_ARCHIVED,
  T_QUESTION_UNARCHIVED,
  T_QUESTION_PINNED,
  T_QUESTION_UNPINNED,
  T_QUESTION_APPROVED,
  T_QUESTION_REPLY_DELETED,
  T_NO_QUESTIONS_PENDING,
} from '@vimeo/live-core/translations';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import {
  EQnaTab,
  EQuestionState,
  IQnAPanel,
  IQnAQuestion,
} from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { FOCUSED_STATE_COLOR } from '@vimeo/live-core/utils/styling/colors';

export function QnaManage({
  id = createInteractionDomName('qna-manage'),
  qnaContext: {
    isHydrated,
    activeSessionId,
    activeSession,
    activeSessionPinnedQuestionId,
    isEventModerated,
    qnaReplies,
    qnaActions: {
      approveQuestion,
      archiveQuestion,
      unArchiveQuestion,
      pinQuestion,
      unPinQuestion,
      deleteQuestionReply,
    },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(QnAManager),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  firebaseContext: { connectionStatuses: { isInteractionConnected } } = useManager(
    InteractionFirebaseManager,
  ),
}) {
  const {
    initialState,
    initialState: { sessionType },
  } = useLiveGlobals<IInteractionStoreInitialState>();
  const {
    selectedQuestionId,
    selectedQuestionState,
    selectedQuestionElement,
    onSelectedQuestion,
    onDeselectQuestion,
  } = useQnaResponding();

  const { panels, currentQuestions, activePanelId, setActivePanelId } = useQnaPanels();
  const { getIdByIndex, getIndexById } = useTabsMapping<IQnAPanel>(panels);

  const notify: TNotificationHandler = useSimpleNotification();

  const isReplyFormVisible: boolean =
    selectedQuestionId !== null &&
    canReplyQuestion(currentQuestions.find((it) => it.id === selectedQuestionId)) &&
    !hasQuestionReplies(selectedQuestionId ? qnaReplies?.[selectedQuestionId] : undefined);

  useEffect(() => {
    trackViewQna();
  }, []);

  useEffect(() => {
    if (isHydrated && activeSessionId && sessionType === EComposerSessionType.VENUE) {
      trackVenuesViewQa(String(activeSessionId));
    }
  }, [activeSessionId, isHydrated, sessionType]);

  const onApproveQuestion = useCallback(
    (question: IQnAQuestion) => {
      approveQuestion(question.id);
      trackApproveModeratedQuestion();
      notify({ message: T_QUESTION_APPROVED });
    },
    [approveQuestion, notify],
  );

  const onArchiveQuestion = useCallback(
    (question: IQnAQuestion) => {
      archiveQuestion(question.id, Boolean(question.approved));
      trackArchiveQuestion(isEventModerated);
      notify({ message: T_QUESTION_ARCHIVED });
    },
    [archiveQuestion, isEventModerated, notify],
  );

  const onUnArchiveQuestion = useCallback(
    (question: IQnAQuestion) => {
      unArchiveQuestion(question.id, Boolean(question.approved));
      trackUnarchiveQuestion(isEventModerated);
      notify({ message: T_QUESTION_UNARCHIVED });
    },
    [isEventModerated, notify, unArchiveQuestion],
  );

  const onPinQuestion = useCallback(
    (questionId: string) => {
      pinQuestion(questionId);
      trackPinQuestion();
      notify({
        message: T_QUESTION_PINNED,
      });
    },
    [notify, pinQuestion],
  );

  const onUnPinQuestion = useCallback(
    (questionId: string) => {
      unPinQuestion(questionId);
      trackUnpinQuestion();
      notify({ message: T_QUESTION_UNPINNED });
    },
    [notify, unPinQuestion],
  );

  const onDeleteQuestionReply = useCallback(
    (questionId: string, replyId: string) => {
      deleteQuestionReply(questionId, replyId);
      // todo: tracking event
      notify({ message: T_QUESTION_REPLY_DELETED });
    },
    [notify, deleteQuestionReply],
  );

  const onChangeTab = useCallback(
    (index: number) => {
      setActivePanelId(getIdByIndex(index) as EQnaTab);
      onDeselectQuestion();
      trackSwitchActiveTab();
    },
    [getIdByIndex, onDeselectQuestion, setActivePanelId],
  );

  if (!isHydrated) {
    return (
      <Flex
        id={id}
        direction={'column'}
        grow={1}
        overflowY={'hidden'}
        position={'relative'}
        height={'100%'}
        padding={`0 ${rem(24)} ${rem(24)} ${rem(24)}`}
      >
        <BlockingLoadingWrapper
          id={createInteractionDomName(id, 'loader')}
          opacity={0.1}
          withAnimation={true}
        />
      </Flex>
    );
  }

  if (activeSession) {
    return (
      <Flex
        id={id}
        direction={'column'}
        grow={1}
        maxHeight={'100%'}
        overflow={'hidden'}
        padding={rem(24)}
      >
        <Tabs
          size={'sm'}
          sx={TABS_STYLES}
          index={getIndexById(activePanelId)}
          onChange={onChangeTab}
        >
          <TabList>
            {panels.map(({ id: panelId, label }) => (
              <Tab key={panelId} className={createInteractionDomName(id, panelId, 'tab')}>
                {label}
              </Tab>
            ))}

            <TabIndicator />
          </TabList>

          <TabPanels key={activePanelId} sx={TAB_PANELS_STYLES}>
            {panels.map(({ id: panelId, questions }) => (
              <TabPanel
                key={panelId}
                className={createInteractionDomName(id, panelId, 'tab-panel')}
                sx={TAB_PANEL_STYLES}
              >
                <QuestionsList
                  id={createInteractionDomName(id, panelId, 'questions-list')}
                  tabId={panelId}
                  selectedQuestionElement={selectedQuestionElement}
                  questions={questions}
                  fullWidth={true}
                  pinnedQuestionId={activeSessionPinnedQuestionId}
                  isManagementAccessed={true}
                  scrollBackground={initialState?.interaction?.backgroundColor || 'background'}
                  itemRenderer={(question: IQnAQuestion): ReactNode => {
                    const sceneItemOverlayColor: string =
                      selectedQuestionId === question.id ? FOCUSED_STATE_COLOR : 'transparent';

                    return (
                      <Box key={question.id} sx={TAB_LIST_COLUMN_ROW_ITEM_STYLE}>
                        <NewQuestionManageItem
                          question={question}
                          isActive={question.id === selectedQuestionId}
                          isPinned={question.id === activeSessionPinnedQuestionId}
                          questionReplies={qnaReplies[question.id]}
                          onQuestionSelected={onSelectedQuestion}
                          onQuestionDeselected={onDeselectQuestion}
                          onQuestionApprove={onApproveQuestion}
                          onQuestionArchive={onArchiveQuestion}
                          onQuestionUnArchive={onUnArchiveQuestion}
                          onQuestionPin={onPinQuestion}
                          onQuestionUnPin={onUnPinQuestion}
                          onDeleteQuestionReply={onDeleteQuestionReply}
                        />

                        <SceneItemOverlay
                          size={'sm'}
                          width={2}
                          borderRadius={8}
                          zIndex={'auto'}
                          color={sceneItemOverlayColor}
                        />
                      </Box>
                    );
                  }}
                  placeholder={
                    <EmptyStatePlaceholder
                      id={createInteractionDomName(id, panelId, 'empty-placeholder')}
                      className={createInteractionDomName(id, panelId, 'empty-placeholder')}
                      isWithPadding={true}
                      icon={<ReviewQuestion boxSize={'lg'} />}
                      description={
                        panelId === EQnaTab.PENDING ? T_NO_QUESTIONS_PENDING : T_NO_QUESTIONS_YET
                      }
                      control={null}
                    />
                  }
                />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>

        <Box id={createInteractionDomName(id, 'controls')} position={'relative'} width={'100%'}>
          <HorizontalScrollShadow
            color={initialState?.interaction?.backgroundColor || 'background'}
          />

          {isReplyFormVisible ? (
            <QnaReplyForm
              id={createInteractionDomName(id, 'reply-form')}
              selectedQuestionId={selectedQuestionId as string}
              isApproveNeeded={selectedQuestionState === EQuestionState.PENDING}
              onDismiss={onDeselectQuestion}
            />
          ) : null}
        </Box>
      </Flex>
    );
  } else {
    return (
      <Flex
        id={id}
        direction={'column'}
        grow={1}
        overflowY={'hidden'}
        position={'relative'}
        height={'100%'}
        padding={`0 ${rem(24)} ${rem(24)} ${rem(24)}`}
      >
        <SessionInactive />
        <Box
          id={createInteractionDomName(id, 'session-controls')}
          position={'relative'}
          width={'100%'}
        />
      </Flex>
    );
  }
}
